// Share Trade-in — pure selection math for the Collection trade-in mode.
//
// Point values by tier (mirrors server/src/services/trade_in_service.rs
// ::tier_points): common = 1, preferred = 2, premium = 3, founders = 8.
// Trading in a selection worth >= 10 points burns those shares and mints
// a random sealed dossier. Overshoot is allowed but FORFEITED — points
// above 10 are not returned.
//
// Pure functions only; no React, no network. Unit-tested in
// tradeInPoints.test.ts.

import type { CertificateShare } from "@greenhold/components";

export const TRADE_IN_POINTS_REQUIRED = 10;

export const TIER_POINTS: Record<string, number> = {
  common: 1,
  preferred: 2,
  premium: 3,
  founders: 8,
};

/// Small legend rendered next to the running total.
export const TIER_POINTS_LEGEND: { tier: string; label: string; points: number }[] = [
  { tier: "common", label: "Common", points: 1 },
  { tier: "preferred", label: "Preferred", points: 2 },
  { tier: "premium", label: "Premium", points: 3 },
  { tier: "founders", label: "Founder's", points: 8 },
];

/// Point value for a single tier string. Unknown tier → 0 (defensive;
/// the backend only ever issues the four canonical tiers).
export function pointsForTier(tier: string): number {
  return TIER_POINTS[tier] ?? 0;
}

/// Sum the tier points for a set of shares.
export function sumPoints(shares: Pick<CertificateShare, "tier">[]): number {
  return shares.reduce((acc, s) => acc + pointsForTier(s.tier), 0);
}

/// Whether a running total meets the trade-in threshold.
export function meetsThreshold(points: number): boolean {
  return points >= TRADE_IN_POINTS_REQUIRED;
}

/// Whether the selection overshoots the threshold (points that will be
/// forfeited). Only meaningful once the threshold is met.
export function isOvershoot(points: number): boolean {
  return points > TRADE_IN_POINTS_REQUIRED;
}

/// Points beyond the threshold that will be forfeited (0 when not over).
export function forfeitedPoints(points: number): number {
  return Math.max(0, points - TRADE_IN_POINTS_REQUIRED);
}
